import type { AnyRouter } from '@tanstack/react-router';
import { router } from '../../router';

/**
 * Navigation utility for programmatic navigation outside of React components
 * This helps avoid window.location usage and maintains SPA behavior
 */
class NavigationService {
  private router: AnyRouter | null = null;

  /**
   * Initialize the navigation service with the router instance
   * This should be called once when the app initializes
   */
  initialize(routerInstance: AnyRouter) {
    this.router = routerInstance;
  }

  /**
   * Navigate to a specific route
   * @param to - The route path to navigate to
   * @param options - Navigation options
   */
  navigate(
    to: string,
    options: { replace?: boolean; resetScroll?: boolean } = {}
  ) {
    if (!this.router) {
      console.error(
        '[NavigationService] Router not initialized. Using default router instance.'
      );
      // Use the default router instance as fallback
      this.router = router;
    }

    try {
      // Use TanStack Router's navigate method
      this.router.navigate({
        to,
        replace: options.replace ?? false,
        resetScroll: options.resetScroll ?? true,
      });
    } catch (error) {
      console.error('[NavigationService] Navigation failed:', error);

      // Only as absolute last resort, and only for critical auth redirects
      // This should rarely happen if the router is properly initialized
      if (to === '/login' || to === '/logout') {
        console.error(
          '[NavigationService] Critical auth redirect failed, degrading to location change'
        );
        // Even here, try to use history.pushState first
        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', to);
          // Trigger a popstate event to notify the router
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      }
    }
  }

  /**
   * Navigate to login page
   * Commonly used for auth failures
   */
  navigateToLogin(replace = true) {
    this.navigate('/login', { replace });
  }

  /**
   * Navigate to home page
   */
  navigateToHome(replace = false) {
    this.navigate('/', { replace });
  }

  /**
   * Get current location pathname
   */
  getCurrentPath(): string {
    return window.location.pathname;
  }

  /**
   * Check if currently on a specific path
   */
  isOnPath(path: string): boolean {
    return this.getCurrentPath() === path;
  }

  /**
   * Check if on an auth-related page
   */
  isOnAuthPage(): boolean {
    const path = this.getCurrentPath();
    return (
      path === '/login' ||
      path === '/logout' ||
      path === '/auth/callback' ||
      path.startsWith('/auth/tenant-limit-exceeded')
    );
  }
}

// Create singleton instance
export const navigationService = new NavigationService();

// Export convenience methods
export const navigateTo = (
  to: string,
  options?: { replace?: boolean; resetScroll?: boolean }
) => navigationService.navigate(to, options);

export const navigateToLogin = (replace = true) =>
  navigationService.navigateToLogin(replace);

export const navigateToHome = (replace = false) =>
  navigationService.navigateToHome(replace);

export const isOnAuthPage = () => navigationService.isOnAuthPage();
